import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useOrg } from '../../contexts/OrgContext';
import { projectsApi, datasetsApi } from '../../lib/api';
import { addLayerParam, datasetIdsFromParams } from '../../lib/map-url';
import { useOptionalToast } from '../Toast';

/** Popover position just below an anchor element, kept inside the viewport (340 px wide). */
export function popoverPosBelow(el) {
  const r = el?.getBoundingClientRect();
  return { top: (r?.bottom ?? 80) + 6, left: Math.max(8, Math.min(r?.left ?? 8, window.innerWidth - 356)) };
}

/** Open a project or dataset on the map: sets ?project= / ?dataset= (other params stay). */
export default function OpenDatasetPopover({ pos, onClose, wrapRef }) {
  const { C } = useTheme();
  const ref = useRef(null);
  const { selectedOrgId } = useOrg() || {};
  const { showToast } = useOptionalToast() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const currentDataset = searchParams.get('dataset');
  const [projectId, setProjectId] = useState(searchParams.get('project') || '');
  const [projects, setProjects] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || wrapRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose, wrapRef]);
  useEffect(() => {
    let live = true;
    projectsApi.list(selectedOrgId)
      .then((r) => { if (live) setProjects(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setError('Could not load projects'); });
    return () => { live = false; };
  }, [selectedOrgId]);
  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    datasetsApi.list({ organization_id: selectedOrgId, project_id: projectId || undefined, page: 1, page_size: 100 })
      .then((r) => { if (live) setDatasets(r.data?.datasets || []); })
      .catch(() => { if (live) setError('Could not load datasets'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [selectedOrgId, projectId]);
  const apply = (project, dataset, name) => {
    const next = new URLSearchParams(searchParams);
    if (project) next.set('project', project); else next.delete('project');
    if (dataset) next.set('dataset', dataset); else next.delete('dataset');
    setSearchParams(next);
    showToast?.(`Opened ${name}`);
    onClose();
  };
  const shown = datasetIdsFromParams(searchParams);
  const addLayer = (id, name) => {
    setSearchParams(addLayerParam(searchParams, id));
    showToast?.(`Added ${name}`);
    onClose();
  };
  const q = filter.trim().toLowerCase();
  const rows = q ? datasets.filter((d) => (d.name || '').toLowerCase().includes(q)) : datasets;
  const inputStyle = { background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 };
  return (
    <div ref={ref} role="dialog" aria-label="Open project or dataset"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 340, maxWidth: 'calc(100vw - 16px)', zIndex: 1000, padding: 10, borderRadius: 8,
        background: C.bg2, border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
      <div className="flex items-center gap-2">
        <select aria-label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}
          className="h-7 px-2 text-[11px] rounded-md outline-none flex-1 min-w-0" style={inputStyle}>
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {projectId && (
          <button type="button" className="h-7 px-2 text-[11px] rounded-md flex-shrink-0" style={inputStyle}
            onClick={() => apply(projectId, null, projects.find((p) => p.id === projectId)?.name || 'project')}>Open project</button>
        )}
      </div>
      {datasets.length > 8 && (
        <input aria-label="Filter datasets" placeholder="Filter datasets" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="h-7 px-2 mt-2 w-full text-[11px] rounded-md outline-none" style={inputStyle} />
      )}
      <div className="mt-2 text-[11px]" style={{ maxHeight: 240, overflowY: 'auto', color: C.text1 }}>
        {error && <div role="alert" style={{ color: C.red }}>{error}</div>}
        {loading ? <div>Loading…</div>
          : rows.length === 0 ? <div>{projectId ? 'No datasets in this project yet' : 'No datasets yet'}</div>
          : rows.map((d) => (
            <div key={d.id} className="flex items-center gap-1 rounded hover:bg-white/5"
              style={{ background: d.id === currentDataset ? C.accentBg : undefined }}>
              <button type="button" data-testid={`open-dataset-${d.id}`} onClick={() => apply(d.project_id, d.id, d.name)}
                className="flex flex-1 min-w-0 items-center justify-between gap-2 px-2 py-1 text-left" style={{ color: C.text0 }}>
                <span className="truncate">{d.name}</span>
                <span className="flex-shrink-0" style={{ color: C.text2 }}>{d.method_type}</span>
              </button>
              {shown.includes(d.id)
                ? <span className="flex-shrink-0 px-2" style={{ color: C.text2 }}>{'✓'} Added</span>
                : (
                  <button type="button" data-testid={`add-dataset-${d.id}`} title="Add to the map as a layer, keeping the current layers"
                    onClick={() => addLayer(d.id, d.name)} className="flex-shrink-0 h-6 px-2 mr-1 rounded" style={inputStyle}>Add</button>
                )}
            </div>
          ))}
      </div>
    </div>
  );
}
